import { IntroStep } from 'intro.js/src/core/steps';

export const dashboardSteps: Partial<IntroStep>[] = [
  {
    element: '[data-testid="provider-dashboard-page"]',
    intro: '👋 Welcome to your Dashboard! Let’s take a quick tour!.',
    title: 'Dashboard',
    position: 'floating',
  },
  {
    title: 'Income Metrics',
    element: '.income-metric-card',
    intro:
      '💰 See your income metrics at a glance. Monitor your earnings in real-time.',
    position: 'top-middle-aligned',
  },
  {
    element: '.paid-monthly-metric',
    intro:
      '📅 Compare this month’s paid amounts with last month to track progress.',
    title: 'Paid Metrics',
  },
  {
    element: '.submitted-monthly-metric',
    intro:
      '📝 Compare your submitted claims for the month with the previous month.',
    title: 'Submitted Metrics',
  },
  {
    element: '.rejected-monthly-metric',
    intro:
      '❌ Check the number of rejected claims up to today and compared to last month.',
    title: 'Rejected Metrics',
  },
  {
    element: '.live-data-metric-card',
    intro: 'This section shows live counts of different claim statuses.',
    title: 'Live Status Counts',
  },
  {
    element: '.monthly-claim-metric',
    intro:
      '📈 This section shows the number of claims this month (up to today) and compared to last month.',
    title: 'Monthly Claims',
  },
  {
    element: '.yearly-claim-metric',
    intro:
      '📈 This section shows the number of claims this year (up to today) and compared to last year.',
    title: 'Yearly Claims',
  },
  {
    element: '.site-selector',
    intro: `<div style="text-align: center;">
            <img src="/Site-Selector.gif" alt="Site Selector" style="width: 100%; max-width: 300px;" />
            <p>🏢 Select a site from this dropdown to filter data by location.</p>
          </div>`,
    title: 'Site Selector',
  },
  {
    element: '.user-avatar-dropdown',
    intro: `<div style="text-align: center;">
            <img src="/Avatar-dropdown.gif" alt="Avatar" style="width: 100%; max-width: 300px;" />
            <p>👤 Click here to view your profile and account settings.</p>
          </div>`,
    title: 'Avatar',
  },
  {
    element: '.color-scheme-toggle',
    intro: `<div style="text-align: center;">
            <img src="/color-toggle.gif" alt="Color Scheme" style="width: 100%; max-width: 300px;" />
          <p>🌗 Switch between light and dark mode.</p>`,
    title: 'Light/Dark Mode Toggle',
  },
  {
    element: '.dashboard-chart',
    intro:
      '📉 This chart provides a visual representation of your claims within the chosen time period.',
    position: 'floating',
    title: 'Dashboard Chart',
  },
  {
    element: '.hamburger-menu',
    intro: `
    <div style="text-align: center;">
      <p>Click here to expand the sidebar. (Pro tip: You can also press the "[" key to toggle it)</p>`,
    title: 'Hamburger Menu',
  },
  {
    element: 'a[data-testid="claims-nav-link"]',
    intro: '➡️ Click here to navigate to the Claims page.',
    title: 'Claims Page',
  },
];

export const claimsSteps: Partial<IntroStep>[] = [
  {
    element: '.root',
    intro: "👋 Welcome to the Claims page! Let's take a quick tour.",
    title: 'Claim Page',
    position: 'top-middle-aligned',
  },
  {
    element: '[data-testid="claimsManagement-table"]',
    intro: 'This table displays all your claims.',
    title: 'Claims Table',
    position: 'floating',
  },
  {
    element: '[data-testid="claim-tool-bar"]',
    intro: 'Use these filters to filter your claims by status.',
    title: 'Claim Filters',
  },
  {
    element: '.internal-refresh-button',
    intro: '🔄 Click here to refresh the table data.',
    title: 'Refresh Table',
  },
  {
    element: '[data-testid="show-action-button"]',
    intro: 'Click here to show or hide actions column.',
    title: 'Show/Hide Actions',
  },
  {
    element: '.global-filter-button',
    intro: '🔍 Click here to toggle the search bar and search the claims.',
    title: 'Global Filter Toggle',
  },
  {
    element: '.show-hide-columns-button',
    intro: '🛠 Customize which columns you see to tailor your view.',
    title: 'Show/Hide Columns',
  },
  {
    element: '.mrt-expand-button',
    intro: 'Click here to expand/collapse the row.',
    title: 'Expand/Collapse Row',
  },
  {
    element: '.mantine-Table-td-detail-panel',
    intro:
      'This is the claim detail panel section, when the history is selected it displays a timeline of this claims history.',
    position: 'top-right-aligned',
    title: 'Claim Detail Panel',
  },
  {
    element: '#claim-detail-panel-segmented-control > div:nth-child(3) > label',
    title: 'Submissions',
  },
  {
    element: '.mantine-Table-td-detail-panel',
    intro: 'This shows the submission history of this claim.',
    title: 'Submission History',
    position: 'bottom-middle-aligned',
  },
  {
    element: '#claim-detail-panel-segmented-control > div:nth-child(4) > label',
    title: 'Debits',
    intro: 'Click here to view the Claim History.',
  },
  {
    element: '.mantine-Table-td-detail-panel',
    intro: 'This shows payments for this claim.',
    title: 'Debits History',
  },
  {
    element: 'a[data-testid="statements-nav-link"]',
    intro: 'Click here to go to the Statements page.',
    title: 'Statements Page',
  },
];

export const reportSteps: Partial<IntroStep>[] = [
  {
    element: '[data-testid="reportManagement-table"]',
    intro: 'This table displays all your reports.',
    title: 'Reports Table',
  },
  {
    element: '[data-testid="report-download-button"]',
    intro: 'Click here to download the report (when available).',
    title: 'Download Report Button',
  },
  {
    element: '[data-testid="create-report-button"]',
    intro: 'Click here to create a new report.',
    title: 'Create Report',
  },
  {
    element: 'a[data-testid="transactions-nav-link"]',
    intro:
      '➡️ Click here to navigate to the Transactions page for more details.',
    title: 'Transactions Page',
  },
];

export const statementSteps: Partial<IntroStep>[] = [
  {
    element: '.root',
    intro: "Welcome to the Statements page! Let's take a quick tour.",
    title: 'Statement Page',
  },
  {
    element: '[data-testid="statementManagement-table"]',
    intro:
      '📊 This table organizes all your statements, allowing you to easily review and manage them.',
    title: 'Statements Table',
  },
  {
    element: '[data-testid="report-download-button"]',
    intro: "⬇️ Click here to download a statement when it's available.",
    title: 'Download Statement',
  },
  {
    element: '.mrt-expand-button',
    intro: '📖 Click here to expand or collapse additional statement details.',
    title: 'Expand/Collapse Row',
  },
  {
    element: '.mantine-Table-td-detail-panel',
    intro:
      '📝 This panel shows a brief summary of the pay statement as well as the individual claims associated with this pay statement',
    title: 'Statement Detail Panel',
  },
  {
    element:
      '#statement-detail-panel-segmented-control > div:nth-child(3) > label',
    title: 'Claims',
  },
  {
    element: '.mantine-Table-td-detail-panel',
    intro: 'This shows the claims associated with this pay statement.',
  },
  {
    element: 'a[data-testid="reports-nav-link"]',
    intro: 'Click here to go to the reports page.',
    title: 'Reports Page',
  },
];

export const transactionSteps: Partial<IntroStep>[] = [
  {
    element: '.root',
    intro:
      "👋 Welcome to the Transactions page! Let's review your transactions in detail.",
    title: 'Transaction Page',
  },
  {
    element: '[data-testid="transactionManagement-table"]',
    intro:
      '📋 This table lists all your transactions, allowing you to track every detail.',
    title: 'Transactions Table',
  },
  {
    element: 'a[data-testid="settings-nav-link"]',
    intro: 'Click here to go to the settings page.',
    title: 'Settings Page',
  },
];

export const settingsSteps: Partial<IntroStep>[] = [
  {
    element: '.settings-page',
    intro: "⚙️ Welcome to the Settings page! Let's take a quick tour.",
    title: 'Settings Page',
  },
  {
    element: '.personal-info',
    intro: 'ℹ️ This section allows you to update your personal information.',
    title: 'Personal Information',
  },
  {
    element: '.password-reset',
    intro: '🔑 This section allows you to update your password.',
    title: 'Password Reset',
  },
  {
    element: '.dashboard-chart-preference',
    intro:
      '📊 This section allows you to update your dashboard chart preferences.',
    title: 'Dashboard Chart Preferences',
  },
  {
    element: '.tutorial-replay',
    intro: '🔁 Click here to replay the tutorial at any time.',
    title: 'Replay Tutorial',
  },
];
